// 💬 AI Assistant — same as the desktop app. Ask about sales, profit, stock, customers and invoices
// in Lebanese, Arabic, English or French. Answers come from the shop's own data. A change (new
// item, new price, draft invoice, customer, expense) is shown as a card: nothing changes until the
// user presses ✓ Confirm, and a confirmed change can be undone.
// Paid add-on (ai_chat), switched on by the platform owner only.
import React, { useEffect, useRef, useState } from 'react';
import api from '../../config/api';

const GREEN = '#2e9f86';
const hasArabic = (s) => /[؀-ۿ]/.test(s || '');
const errOf = (e) => e.response?.data?.error || e.message;
const SUGGEST = ['كم بعنا اليوم؟', 'Profit this month', 'Which items are running low?', 'Who owes us money?', 'Best selling items this month', 'Add item Pepsi 330ml price $0.75'];

export default function Assistant() {
  const [threads, setThreads] = useState([]);
  const [threadId, setThreadId] = useState(null);
  const [msgs, setMsgs] = useState([]);            // { role, text, proposals? }
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState('');
  const [speak, setSpeak] = useState(false);          // 🔊 read answers aloud
  const [rec, setRec] = useState(null);               // 🎤 { recorder, chunks, started }
  const [recSec, setRecSec] = useState(0);
  const endRef = useRef(null);
  useEffect(() => {
    if (!rec) return undefined;
    const t = setInterval(() => setRecSec(Math.round((Date.now() - rec.started) / 1000)), 500);
    return () => clearInterval(t);
  }, [rec]);

  const loadThreads = async () => {
    try { const r = await api.get('/api/analysis/chat/threads'); setThreads(Array.isArray(r.data) ? r.data : []); }
    catch (e) { if (e.response?.status === 403) setBlocked(errOf(e)); }
  };
  useEffect(() => { loadThreads(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs, busy]);

  const open = async (id) => {
    if (busy) return;
    try {
      const r = await api.get(`/api/analysis/chat/${id}`);
      setThreadId(id); setMsgs(r.data.messages || []);
    } catch (e) { alert(errOf(e)); }
  };
  const newChat = () => { setThreadId(null); setMsgs([]); };
  const remove = async (id) => {
    if (!window.confirm('Delete this chat?')) return;
    try { await api.delete(`/api/analysis/chat/${id}`); if (id === threadId) newChat(); loadThreads(); }
    catch (e) { alert(errOf(e)); }
  };

  const playAudio = (b64) => { try { new Audio(`data:audio/wav;base64,${b64}`).play(); } catch { /* no sound */ } };

  const askServer = async (path, body, shownText) => {
    setBusy(true);
    if (shownText) setMsgs((m) => [...m, { role: 'user', text: shownText }]);
    try {
      const r = await api.post(path, { thread_id: threadId, speak, ...body }, { timeout: 180000 });
      const isNew = !threadId;
      setThreadId(r.data.thread_id);
      setMsgs((m) => [...m, ...(r.data.transcript ? [{ role: 'user', text: '🎤 ' + r.data.transcript }] : []), { role: 'assistant', text: r.data.reply, proposals: r.data.proposals || [] }]);
      if (r.data.audio_reply) playAudio(r.data.audio_reply);
      if (isNew) loadThreads();
    } catch (e) {
      setMsgs((m) => [...m, { role: 'assistant', text: '⚠ ' + errOf(e) }]);
      if (e.response?.data?.reason === 'addon' || e.response?.data?.reason === 'link') setBlocked(errOf(e));
    } finally { setBusy(false); }
  };

  const send = async (q) => {
    const message = (q ?? text).trim();
    if (!message || busy) return;
    setText('');
    await askServer('/api/analysis/chat', { message }, message);
  };

  // 🎤 click to start, click again to send (stops by itself after 90 s)
  const toggleMic = async () => {
    if (rec) { rec.recorder.stop(); return; }
    if (busy) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];
      const started = Date.now();
      recorder.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRec(null); setRecSec(0);
        const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
        if (Date.now() - started < 800 || blob.size < 2000) { setMsgs((m) => [...m, { role: 'assistant', text: '🎤 Too short — click the mic, speak, then click it again.' }]); return; }
        const b64 = await new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(String(fr.result).split(',')[1]); fr.readAsDataURL(blob); });
        const fmt = (recorder.mimeType || '').includes('ogg') ? 'ogg' : (recorder.mimeType || '').includes('mp4') ? 'mp4' : 'webm';
        await askServer('/api/analysis/chat/voice', { audio: b64, format: fmt }, null);
      };
      recorder.start();
      setTimeout(() => { if (recorder.state === 'recording') recorder.stop(); }, 90000);
      setRec({ recorder, chunks, started });
    } catch (e) { alert('Could not use the microphone: ' + e.message); }
  };

  const act = async (pid, action) => {
    if (action === 'undo' && !window.confirm('Undo this change?')) return;
    try {
      const r = await api.post(`/api/analysis/chat/${threadId}/proposals/${pid}/${action}`, {}, { timeout: 60000 });
      const p = r.data.proposal;
      setMsgs((m) => m.map((x) => (x.proposals ? { ...x, proposals: x.proposals.map((y) => (y.id === p.id ? p : y)) } : x)));
    } catch (e) { alert(errOf(e)); }
  };

  return (
    <div style={{ display: 'flex', height: '100%', background: '#f0f5f3' }}>
      {/* conversations */}
      <div className="hidden md:flex" style={{ width: 230, background: '#fff', borderRight: '1px solid #e3eae7', padding: 10, flexDirection: 'column', gap: 8 }}>
        <button onClick={newChat} style={{ background: GREEN, color: '#fff', border: 'none', borderRadius: 9, padding: '10px 0', fontWeight: 700, cursor: 'pointer' }}>＋ New chat</button>
        <div style={{ color: '#6b7280', fontSize: 12, marginTop: 6 }}>Previous chats</div>
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {threads.map((t) => (
            <div key={t.id} onClick={() => open(t.id)} title={t.title}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 8px', borderRadius: 8, cursor: 'pointer', fontSize: 13, background: t.id === threadId ? '#e6f4f0' : 'transparent' }}>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</span>
              <span onClick={(e) => { e.stopPropagation(); remove(t.id); }} style={{ color: '#9ca3af', fontSize: 12 }}>🗑</span>
            </div>
          ))}
        </div>
      </div>

      {/* chat */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div style={{ padding: '14px 20px 6px' }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#1f2937' }}>💬 AI Assistant</div>
          <div style={{ color: '#6b7280', fontSize: 12.5 }}>Ask anything about your sales, profit, stock, customers or invoices — in Lebanese, Arabic, English or French. Changes happen only when you press Confirm.</div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '6px 20px 10px' }}>
          {blocked ? <Bubble text={'⚠ ' + blocked} /> : !msgs.length && (<>
            <Bubble text="Hi! 👋  Ask me about your shop. For example:" />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '4px 0 10px' }}>
              {SUGGEST.map((q) => <button key={q} onClick={() => send(q)} style={{ background: '#fff', border: '1px solid #d1d5db', borderRadius: 20, padding: '6px 12px', fontSize: 13, cursor: 'pointer' }}>{q}</button>)}
            </div>
          </>)}
          {msgs.map((m, i) => (
            <React.Fragment key={i}>
              <Bubble text={m.text} mine={m.role === 'user'} />
              {(m.proposals || []).map((p) => <ProposalCard key={p.id} p={p} onAct={act} />)}
            </React.Fragment>
          ))}
          {busy && <Bubble text="…  thinking" />}
          <div ref={endRef} />
        </div>
        <div style={{ padding: '0 20px', display: 'flex', justifyContent: 'flex-end' }}>
          <label style={{ fontSize: 12, color: '#6b7280', cursor: 'pointer' }}><input type="checkbox" checked={speak} onChange={(e) => setSpeak(e.target.checked)} /> 🔊 Read answers aloud</label>
        </div>
        <div style={{ padding: '4px 20px 14px', display: 'flex', gap: 8 }}>
          <textarea value={text} disabled={!!blocked} onChange={(e) => setText(e.target.value)} dir={hasArabic(text) ? 'rtl' : 'ltr'}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Type your question… (Enter = send · Shift+Enter = new line)" rows={2}
            style={{ flex: 1, resize: 'none', border: '1px solid #d1d5db', borderRadius: 10, padding: '10px 12px', fontSize: 15, outline: 'none' }} />
          <button onClick={toggleMic} disabled={(busy && !rec) || !!blocked} title="Talk: click, speak, then click again to send"
            style={{ background: rec ? '#ef4444' : '#fff', color: rec ? '#fff' : '#111', border: '1px solid #d1d5db', borderRadius: 10, padding: '0 14px', fontSize: rec ? 14 : 22, fontWeight: 700, cursor: 'pointer', minWidth: 56 }}>
            {rec ? `■ ${recSec}s` : '🎤'}
          </button>
          <button onClick={() => send()} disabled={busy || !!blocked} style={{ background: GREEN, color: '#fff', border: 'none', borderRadius: 10, padding: '0 20px', fontWeight: 700, fontSize: 15, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>Send ➤</button>
        </div>
      </div>
    </div>
  );
}

function Bubble({ text, mine }) {
  return (
    <div style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
      <div dir={hasArabic(text) ? 'rtl' : 'ltr'} style={{ maxWidth: '78%', background: mine ? '#dcf3ec' : '#fff', border: mine ? 'none' : '1px solid #e3eae7', borderRadius: 12, padding: '9px 12px', fontSize: 14.5, color: '#1f2937', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{text}</div>
    </div>
  );
}

function ProposalCard({ p, onAct }) {
  const [busy, setBusy] = useState(false);
  const run = async (a) => { setBusy(true); try { await onAct(p.id, a); } finally { setBusy(false); } };
  const status = p.status === 'done' ? { t: '✓ ' + (p.result || 'Done'), c: '#1e6f5d', b: true }
    : p.status === 'cancelled' ? { t: '✗ Cancelled — nothing changed', c: '#6b7280' }
    : p.status === 'undone' ? { t: '↶ Undone', c: '#6b7280' }
    : { t: 'Nothing changes until you confirm.', c: '#6b7280' };
  return (
    <div style={{ background: '#fff', border: '1px solid #e3eae7', borderLeft: `4px solid ${GREEN}`, borderRadius: 12, padding: 14, margin: '0 0 10px', maxWidth: 520 }}>
      <div style={{ fontWeight: 700, fontSize: 14.5, color: '#1f2937' }}>📝 {p.title}</div>
      <ul style={{ margin: '6px 0 6px 18px', padding: 0, fontSize: 13.5, color: '#374151' }}>
        {(p.lines || []).map((l, i) => <li key={i} style={{ color: String(l).startsWith('⚠') ? '#b45309' : undefined }}>{l}</li>)}
      </ul>
      <div style={{ fontSize: 12.5, color: status.c, fontWeight: status.b ? 700 : 400 }}>{status.t}</div>
      {p.error && p.status === 'pending' && <div style={{ fontSize: 12.5, color: '#ef4444', marginTop: 4 }}>⚠ {p.error}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        {p.status === 'pending' && (<>
          <button disabled={busy} onClick={() => run('confirm')} style={{ background: GREEN, color: '#fff', border: 'none', borderRadius: 8, padding: '7px 18px', fontWeight: 700, cursor: 'pointer' }}>✓ Confirm</button>
          <button disabled={busy} onClick={() => run('cancel')} style={{ background: '#fff', border: '1px solid #d1d5db', borderRadius: 8, padding: '7px 18px', cursor: 'pointer' }}>✗ Cancel</button>
        </>)}
        {p.status === 'done' && p.can_undo && <button disabled={busy} onClick={() => run('undo')} style={{ background: '#fff', border: '1px solid #d1d5db', borderRadius: 8, padding: '7px 18px', cursor: 'pointer' }}>↶ Undo</button>}
      </div>
    </div>
  );
}
